import { useCallback, useMemo, useState } from 'react'

import { extractInputProps, type TypeaheadOptionType } from '@/defs/input'
import { useTypeaheadKeyboardNav } from '@/hooks/useTypeaheadKeyboardNav'
import { mergeRefs } from '@/utils/commonUtil'
import { filterTypeahead } from '@/utils/typeaheadUtil'

import { type Props as ContentProps } from './InputTypeaheadContent'

type Props<T extends string = string> = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
} & ContentProps<T>

export const useTypeaheadContent = <T extends string = string>({
  autoFocus,
  disabled,
  icon = 'search',
  isOpen,
  loading,
  minCharacters = 0,
  onClear,
  onSearch,
  onSelect,
  optionGroups: optionGroupsProp,
  options,
  placeholder,
  ref: refProp,
  required,
  reset,
  setIsOpen,
  showLoadingMask,
  value,
  ...props
}: Props<T>) => {
  const { inputProps } = extractInputProps(props)
  const [search, setSearchInternal] = useState<string>('')

  const setSearch = useCallback(
    (next: string) => {
      setSearchInternal(next)
      onSearch?.(next)
    },
    [onSearch],
  )

  const optionGroups = useMemo(
    () => [...(optionGroupsProp || []), ...(options ? [{ options }] : [])],
    [optionGroupsProp, options],
  )

  const { groups, resultsCount } = useMemo(
    () => filterTypeahead({ disableFiltering: !!onSearch, minCharacters, optionGroups, reset, search, value }),
    [onSearch, minCharacters, search, optionGroups, reset, value],
  )

  const { onKeyDownButton, onKeyDownInput, refInput, setRefButton } = useTypeaheadKeyboardNav(resultsCount)

  const ref = useMemo(() => mergeRefs(refProp, refInput), [refProp, refInput])

  const optionCurrent = useMemo(
    (): Partial<TypeaheadOptionType<T>> =>
      optionGroups.flatMap((g) => g.options).find((o) => o.value === value) || (value ? { value } : {}),
    [optionGroups, value],
  )

  const onSelectInternal = (selected: T) => {
    if (disabled) return
    if (onSelect) {
      const label = optionGroups.flatMap((g) => g.options).find((o) => o.value === selected)?.label
      onSelect(selected, label)
    }
    setSearch('')
    setIsOpen(false)
  }

  return {
    'aria-label': props['aria-label'],
    autoFocus,
    disabled,
    groups,
    hasMinCharacters: search.length >= minCharacters,
    hasResults: groups.length > 0,
    icon,
    inputProps,
    isEmpty: !value,
    isOpen,
    loading,
    onClear,
    onKeyDownButton,
    onKeyDownInput,
    onSelect: onSelectInternal,
    optionCurrent,
    placeholder,
    ref,
    refInput,
    required,
    search,
    setIsOpen,
    setRefButton,
    setSearch,
    showLoadingMask,
    value,
  }
}
